import { put, call, select } from 'redux-saga/effects';
import cloneDeep from 'lodash/cloneDeep';
import * as types from '../../constants/ActionTypes';
import Api from '../../functions/Api';
import getQuestById from '../../functions/getQuestById';
import enableContentVisibility from '../../functions/quest/enableContentVisibility';
import convertEventsNext from '../../functions/quest/convertEventsNext';

/**
 * Possible actions dispatched at the end of the `syncQuest` saga
 * @type {Array[String]}
 */
export const SYNC_ENDINGS = [
  types.SYNC_QUEST_SUCCESS,
  types.SYNC_QUEST_FAILURE,
];

/**
 * Possible actions dispatched at the end of the `fetchQuest` saga
 * @type {Array[String]}
 */
export const FETCH_ENDINGS = [types.RECEIVE_QUEST, types.NOT_RECEIVE_QUEST];

/**
 * Checks if action is last dispatched of the `syncQuest`
 * @param {Object} action - current action to check
 * @returns {Boolean}
 */
export function isSyncQuestEnding(action) {
  return SYNC_ENDINGS.includes(action.type);
}

/**
 * Checks if action is last dispatched of the `fetchQuest`
 * @param {Object} action - current action to check
 * @returns {Boolean}
 */
export function isFetchQuestEnding(action) {
  return FETCH_ENDINGS.includes(action.type);
}

export function fetchUpdatedQuest(id) {
  return {
    type: types.FETCH_UPDATED_QUEST,
    id,
  };
}

export function triggerUpdateQuestData(questId) {
  return {
    type: types.UPDATE_QUEST_DATA,
    questId,
  };
}

/**
 * Recursively removes lastModified and lastModifiedText fields from an object
 * to prevent timestamp validation errors when sending to server
 */
export function removeTimestampFields(obj) {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => removeTimestampFields(item));
  }

  const cleaned = {};
  Object.keys(obj).forEach((key) => {
    if (key !== 'lastModified' && key !== 'lastModifiedText') {
      cleaned[key] = removeTimestampFields(obj[key]);
    }
  });

  return cleaned;
}

export function* syncQuest({ onSyncStart = () => {}, ...action }) {
  const { id, updateMediaSize } = action;
  const { routes, ...quest } = yield select((state) =>
    getQuestById(state.quests.quests, id)
  );

  yield put({ type: types.SEND_QUEST, id });
  onSyncStart({ questId: id });

  try {
    const routeGuids = (routes || [])
      .map((p) => p.guid)
      .filter((guid) => guid != null && guid !== '');

    const { data } = yield call(Api.put, `quests/${id}/`, {
      data: {
        ...enableContentVisibility(
          convertEventsNext(cloneDeep(quest)),
          'image'
        ),
        route: routeGuids,
        updateMediaSize,
      },
    });
    yield put({
      type: types.SYNC_QUEST_SUCCESS,
      id: data.id,
      lastModified: data.lastModified,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    if (error.status === 400) {
      yield put({ type: types.FETCH_QUEST, id });
    }
    yield put({ type: types.SYNC_QUEST_FAILURE, id });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

export function* updateQuestData(action) {
  const { questId } = action;
  try {
    const quest = yield select((state) =>
      state.quests.quests.find((q) => q.id === questId)
    );

    if (quest) {
      const processedQuest = enableContentVisibility(
        convertEventsNext(cloneDeep(quest)),
        'image'
      );

      const routeGuids = (processedQuest.routes || [])
        .map((p) => p.guid)
        .filter((guid) => guid != null && guid !== '');

      const {
        routes: _routes,
        copies: _copies,
        ...questWithoutRoutes
      } = processedQuest;

      const cleanedQuest = removeTimestampFields(questWithoutRoutes);

      const dataToUpdate = {
        ...cleanedQuest,
        route: routeGuids,
      };

      const response = yield call(Api.put, `quests/${questId}/`, {
        data: dataToUpdate,
      });

      if (response && response.id) {
        yield put({
          type: types.RECEIVE_QUEST,
          quest: { ...quest, ...dataToUpdate },
        });
        yield call(fetchUpdatedQuest, questId);
      } else {
        throw new Error('Server failed to update quest.');
      }
    } else {
      yield put({ type: types.NOT_RECEIVE_QUEST, id: questId });
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('[updateQuestData] Error updating quest:', {
        questId,
        status: error.status,
        message: error.message,
      });
    }
    yield put({ type: types.NETWORK_ERROR, error });
  }
}
